import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FORMULAS } from '../config/formulas';
import { createGiftCard, getGiftCard } from '../services/dataService';
import { sendGiftCardEmail } from '../services/emailService';
import { createStripeCheckout } from '../config/payment';
import '../styles/GiftCard.css';

// ============================================
// PAGE 1 : Acheter une carte cadeau
// ============================================
export function GiftCardPurchase() {
  const [step, setStep] = useState(1); // 1=choix, 2=infos, 3=confirmation
  const [giftType, setGiftType] = useState('formula'); // 'formula' ou 'amount'
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [customAmount, setCustomAmount] = useState(25);
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [giftCardCreated, setGiftCardCreated] = useState(null);
  const [stripeUrl, setStripeUrl] = useState(null);

  const individualFormulas = FORMULAS.filter(f => f.category === 'individual');
  
  const totalPrice = giftType === 'formula' && selectedFormula 
    ? selectedFormula.price 
    : customAmount;

  // Générer un code cadeau unique
  const generateGiftCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'LB-';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleSubmit = async () => {
    if (!senderName || !recipientName) {
      alert("Veuillez remplir les noms de l'acheteur et du bénéficiaire.");
      return;
    }

    setIsSubmitting(true);
    try {
      const giftCode = generateGiftCode();
      
      const giftCardData = {
        code: giftCode,
        type: giftType,
        formulaId: giftType === 'formula' ? selectedFormula?.id : null,
        formulaName: giftType === 'formula' ? selectedFormula?.name : null,
        amount: totalPrice,
        senderName,
        senderEmail,
        recipientName,
        recipientEmail,
        message: message || `Une session de Laser Tag en plein air t'attend chez LaserBay !`,
        status: 'pending', // En attente de paiement
        isPaid: false,
        paymentMethod: 'stripe',
        validUntil: (() => {
          const d = new Date();
          d.setFullYear(d.getFullYear() + 1);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        })(),
      };

      // 1. Créer la carte cadeau (statut pending)
      const result = await createGiftCard(giftCardData);
      
      if (result) {
        const giftWithId = { ...giftCardData, id: result.id || result };
        setGiftCardCreated(giftWithId);

        // 2. Créer la session Stripe pour le paiement
        try {
          const stripeData = {
            id: giftWithId.id,
            formulaName: `Carte cadeau LaserBay — ${giftType === 'formula' ? selectedFormula?.name : 'Montant libre'}`,
            totalPrice: totalPrice,
            contactName: senderName,
            contactEmail: senderEmail,
            isGiftCard: true,
            giftCode: giftCode
          };
          
          const checkout = await createStripeCheckout(stripeData);
          
          if (checkout.url) {
            setStripeUrl(checkout.url);
            setStep(3); // Page avec bouton de paiement
          } else {
            throw new Error('URL Stripe non reçue');
          }
        } catch (stripeErr) {
          console.error("Erreur Stripe:", stripeErr);
          // Fallback : montrer la carte avec instruction de paiement
          setStep(3);
        }
      }
    } catch (err) {
      alert("Erreur lors de la création de la carte cadeau.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="giftcard-container">
      <a href="https://laserbay.net" className="gc-back-site" target="_top">
        ← Retour sur laserbay.net
      </a>

      <div className="gc-header">
        <h2>🎁 Offrir une session LaserBay</h2>
        <p>Faites plaisir avec une carte cadeau Laser Tag en plein air !</p>
      </div>

      {/* ÉTAPE 1 : Choix du cadeau */}
      {step === 1 && (
        <div className="gc-step">
          <h3>Que souhaitez-vous offrir ?</h3>
          
          <div className="gc-type-choice">
            <div 
              className={`gc-type-card ${giftType === 'formula' ? 'selected' : ''}`}
              onClick={() => setGiftType('formula')}
            >
              <span className="gc-type-icon">🎯</span>
              <span className="gc-type-name">Une formule</span>
              <span className="gc-type-desc">Offrir une session précise</span>
            </div>
            <div 
              className={`gc-type-card ${giftType === 'amount' ? 'selected' : ''}`}
              onClick={() => setGiftType('amount')}
            >
              <span className="gc-type-icon">💰</span>
              <span className="gc-type-name">Un montant</span>
              <span className="gc-type-desc">Le bénéficiaire choisit</span>
            </div>
          </div>

          {giftType === 'formula' && (
            <div className="gc-formulas">
              {individualFormulas.map(formula => (
                <div 
                  key={formula.id}
                  className={`gc-formula-card ${selectedFormula?.id === formula.id ? 'selected' : ''}`}
                  onClick={() => setSelectedFormula(formula)}
                >
                  <span className="gc-formula-icon">{formula.icon}</span>
                  <div className="gc-formula-info">
                    <span className="gc-formula-name">{formula.name}</span>
                    <span className="gc-formula-desc">{formula.description}</span>
                  </div>
                  <span className="gc-formula-price">{formula.price}€</span>
                </div>
              ))}
            </div>
          )}

          {giftType === 'amount' && (
            <div className="gc-amount-choice">
              <div className="gc-amount-presets">
                {[15, 25, 50, 75].map(amount => (
                  <button 
                    key={amount}
                    className={`gc-amount-btn ${customAmount === amount ? 'selected' : ''}`}
                    onClick={() => setCustomAmount(amount)}
                  >
                    {amount}€
                  </button>
                ))}
              </div>
              <div className="gc-amount-custom">
                <label>Ou montant personnalisé :</label>
                <input 
                  type="number" 
                  value={customAmount} 
                  onChange={(e) => setCustomAmount(Math.max(10, parseInt(e.target.value) || 10))}
                  min="10"
                  className="gc-amount-input"
                />
                <span>€</span>
              </div>
            </div>
          )}

          <div className="gc-price-display">
            <span>Total :</span>
            <span className="gc-total">{totalPrice}€</span>
          </div>

          <button 
            className="gc-next-btn"
            onClick={() => setStep(2)}
            disabled={giftType === 'formula' && !selectedFormula}
          >
            Continuer →
          </button>
        </div>
      )}

      {/* ÉTAPE 2 : Infos acheteur et bénéficiaire */}
      {step === 2 && (
        <div className="gc-step">
          <h3>Informations</h3>

          <div className="gc-form-section">
            <h4>🎁 De la part de</h4>
            <input 
              type="text" 
              placeholder="Votre nom *"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              className="gc-input"
            />
            <input 
              type="email" 
              placeholder="Votre email (pour le reçu)"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              className="gc-input"
            />
          </div>

          <div className="gc-form-section">
            <h4>🎯 Pour</h4>
            <input 
              type="text" 
              placeholder="Nom du bénéficiaire *"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              className="gc-input"
            />
            <input 
              type="email" 
              placeholder="Email du bénéficiaire (pour envoyer la carte)"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="gc-input"
            />
          </div>

          <div className="gc-form-section">
            <h4>💬 Message personnel</h4>
            <textarea 
              placeholder="Un petit mot pour accompagner le cadeau..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="gc-textarea"
              rows={3}
            />
          </div>

          <div className="gc-price-display">
            <span>Total à régler :</span>
            <span className="gc-total">{totalPrice}€</span>
          </div>

          <div className="gc-actions">
            <button className="gc-back-btn" onClick={() => setStep(1)}>← Retour</button>
            <button 
              className="gc-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting || !senderName || !recipientName}
            >
              {isSubmitting ? 'Préparation du paiement...' : '💳 Payer et offrir — ' + totalPrice + '€'}
            </button>
          </div>
        </div>
      )}

      {/* ÉTAPE 3 : Paiement Stripe */}
      {step === 3 && giftCardCreated && (
        <div className="gc-step gc-payment">
          <div className="gc-payment-icon">💳</div>
          <h3>Finalisez votre achat</h3>
          
          {/* Aperçu de la carte */}
          <div className="gc-card-preview gc-card-mini">
            <div className="gc-card-visual">
              <div className="gc-card-top">
                <span className="gc-card-logo">🎯 LaserBay</span>
                <span className="gc-card-badge">CARTE CADEAU</span>
              </div>
              <div className="gc-card-amount">{giftCardCreated.amount}€</div>
              <div className="gc-card-code">{giftCardCreated.code}</div>
              <div className="gc-card-for">
                Pour : <strong>{giftCardCreated.recipientName}</strong>
              </div>
              <div className="gc-card-from">
                De : {giftCardCreated.senderName}
              </div>
            </div>
          </div>

          <p className="gc-payment-info">
            Après le paiement, la carte cadeau sera <strong>activée automatiquement</strong> 
            {recipientEmail && <> et envoyée par email à <strong>{recipientEmail}</strong></>}.
          </p>

          {stripeUrl ? (
            <a 
              href={stripeUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="gc-stripe-btn"
            >
              💳 Payer {totalPrice}€ par carte bancaire
            </a>
          ) : (
            <div className="gc-stripe-fallback">
              <p>Le paiement en ligne n'est pas disponible pour le moment.</p>
              <p>Contactez-nous au <strong>02 44 84 99 96</strong> pour finaliser l'achat.</p>
              <p>Code de votre carte : <strong>{giftCardCreated.code}</strong></p>
            </div>
          )}

          <p className="gc-secure-notice">
            🔒 Paiement 100% sécurisé via Stripe
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// PAGE 2 : Consulter une carte cadeau
// ============================================
export function GiftCardView() {
  const { giftCode } = useParams();
  const [giftCard, setGiftCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getGiftCard(giftCode);
        if (data) {
          setGiftCard(data);
          
          // Si la carte vient d'être payée et l'email n'a pas encore été envoyé
          if (data.status === 'active' && data.emailPending && data.recipientEmail && !emailSent) {
            try {
              await sendGiftCardEmail(data);
              // Marquer l'email comme envoyé dans Firestore
              const { updateGiftCardEmailSent } = await import('../services/dataService');
              await updateGiftCardEmailSent(data.id);
              setEmailSent(true);
              console.log('Email carte cadeau envoyé à', data.recipientEmail);
            } catch (emailErr) {
              console.error('Erreur envoi email carte cadeau:', emailErr);
            }
          }
        } else {
          setError("Carte cadeau introuvable. Vérifiez le code.");
        }
      } catch (err) {
        setError("Impossible de charger la carte cadeau.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [giftCode]);

  if (loading) return <div className="giftcard-container"><p className="gc-loading">Chargement...</p></div>;
  if (error) return <div className="giftcard-container"><p className="gc-error">{error}</p></div>;

  const isExpired = giftCard.validUntil < new Date().toISOString().split('T')[0];
  const isUsed = giftCard.status === 'used';
  const isPending = giftCard.status === 'pending';
  const isPaid = giftCard.status === 'active' && giftCard.isPaid;
  const justPaid = window.location.search.includes('paid=true');

  return (
    <div className="giftcard-container">
      <a href="https://laserbay.net" className="gc-back-site" target="_top">
        ← Retour sur laserbay.net
      </a>

      {/* Message de confirmation après paiement */}
      {justPaid && isPaid && (
        <div className="gc-payment-success">
          <span className="gc-payment-success-icon">✅</span>
          <h3>Paiement confirmé !</h3>
          <p>La carte cadeau est maintenant active
            {giftCard.recipientEmail && <> et a été envoyée à <strong>{giftCard.recipientEmail}</strong></>}.
          </p>
        </div>
      )}

      {/* Carte en attente de paiement */}
      {isPending && (
        <div className="gc-pending-notice">
          <span>⏳</span>
          <p>Cette carte cadeau est en attente de paiement. Si vous venez de payer, veuillez patienter quelques instants puis rafraîchir la page.</p>
        </div>
      )}

      <div className="gc-card-preview">
        <div className={`gc-card-visual ${isUsed ? 'used' : ''} ${isExpired ? 'expired' : ''}`}>
          <div className="gc-card-top">
            <span className="gc-card-logo">🎯 LaserBay</span>
            <span className="gc-card-badge">
              {isUsed ? 'UTILISÉE' : isExpired ? 'EXPIRÉE' : 'CARTE CADEAU'}
            </span>
          </div>
          <div className="gc-card-amount">{giftCard.amount}€</div>
          {giftCard.formulaName && (
            <div className="gc-card-formula">Formule {giftCard.formulaName}</div>
          )}
          <div className="gc-card-code">{giftCard.code}</div>
          <div className="gc-card-for">
            Pour : <strong>{giftCard.recipientName}</strong>
          </div>
          <div className="gc-card-from">
            De la part de : {giftCard.senderName}
          </div>
          {giftCard.message && (
            <div className="gc-card-message">"{giftCard.message}"</div>
          )}
          <div className="gc-card-valid">
            Valable jusqu'au {new Date(giftCard.validUntil + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>

      {!isUsed && !isExpired && (
        <a href="https://laserbay.net/reservation.html" className="gc-book-btn">
          Réserver ma session 🎯
        </a>
      )}

      <p className="gc-instructions">
        Mentionnez le code <strong>{giftCard.code}</strong> lors de votre réservation 
        ou appelez-nous au <strong>02 44 84 99 96</strong>.
      </p>
    </div>
  );
}
